// Consult-ops helpers (nsofar docs/33) — shared by the Consult Ops queue and
// the SR-detail consult panel. Wire types come from @hcm/core; this file only
// adds display formatting + ApiError body extraction.

import { ApiError } from "./api.js";

// Body shape our /consult-ops relay returns on nsofar rejections (§7.6 inner
// keys pass through: already_confirmed / not_choosable / unknown_option).
export interface ConsultActionErrorBody {
  error?: string;
  message?: string;
  chosen_option_id?: string;
  [k: string]: unknown;
}

export function consultErrorBody(err: unknown): ConsultActionErrorBody | null {
  if (err instanceof ApiError && err.body && typeof err.body === "object") {
    return err.body as ConsultActionErrorBody;
  }
  return null;
}

// Queue age column — coordinators work oldest-first (no SLA by design, so age
// is the visibility mechanism per docs/32 §5).
export function formatAge(createdAtIso: string): string {
  const ms = Date.now() - new Date(createdAtIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

// "YYYY-MM-DDTHH:MM" (IST-naive wire format) → human label. The wire value is
// already IST, so this is pure string formatting — no timezone math.
export function formatIstStamp(datetimeIst?: string | null): string {
  if (!datetimeIst) return "—";
  const [date, time] = datetimeIst.split("T");
  if (!date || !time) return `${datetimeIst} IST`;
  const d = new Date(`${date}T00:00:00`);
  const label = Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  return `${label}, ${time} IST`;
}

export function formatRangeIst(r: { date: string; start: string; end: string }): string {
  const d = new Date(`${r.date}T00:00:00`);
  const label = Number.isNaN(d.getTime())
    ? r.date
    : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  return `${label}, ${r.start}–${r.end} IST`;
}

// amount_minor is in the smallest currency unit (paise/cents).
export function formatAmountMinor(amountMinor?: number | null, currency?: string | null): string {
  if (amountMinor == null) return "—";
  const major = (amountMinor / 100).toFixed(2);
  return `${currency ?? ""} ${major}`.trim();
}
